import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button, Card, CardBody, Container, Nav, NavItem, NavLink, Spinner } from "reactstrap";
import ExpenseManagerService from "../../../Services/expenseManager/ExpenseManagerService";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import DashboardTab from "./DashboardTab";
import ExpensesTab from "./ExpensesTab";
import CategoriesTab from "./CategoriesTab";
import RecurringTab from "./RecurringTab";
import ExpenseFormModal from "./ExpenseFormModal";
import { can, PERMISSIONS } from "./expenseManagerUtils";
import "./expense-manager.scss";

const TABS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "expenses", label: "Expenses" },
  { key: "categories", label: "Categories" },
  { key: "recurring", label: "Recurring" },
];

const FILTER_KEYS = [
  "q", "company_id", "status", "payment_status", "accounting_status", "category_id",
  "currency", "expense_date_from", "expense_date_to",
];

/** Keeps only known, non-empty filters for the expenses/dashboard API. */
export const toApiParams = (filters) => {
  const params = {};
  FILTER_KEYS.forEach((key) => {
    const value = filters[key];
    if (value !== undefined && value !== null && value !== "") params[key] = value;
  });
  return params;
};

export default function ExpenseManagerPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const tab = TABS.some((t) => t.key === searchParams.get("tab")) ? searchParams.get("tab") : "dashboard";

  const setTab = (key) => {
    const next = new URLSearchParams(searchParams);
    next.delete("page");
    if (key === "dashboard") next.delete("tab");
    else next.set("tab", key);
    setSearchParams(next, { replace: true });
  };

  const metaQuery = useQuery({
    queryKey: ["expenseManagerMeta"],
    queryFn: ExpenseManagerService.getMeta,
    staleTime: 60_000,
  });
  const meta = metaQuery.data?.data ?? metaQuery.data;

  const refreshAll = () => setRefreshKey((value) => value + 1);

  if (metaQuery.isLoading) {
    return (
      <Container fluid className="expense-manager-page py-5 text-center">
        <Spinner />
      </Container>
    );
  }

  if (metaQuery.isError && !meta) {
    return (
      <Container fluid className="expense-manager-page py-5">
        <div className="alert alert-danger">
          {getApiErrorMessage(metaQuery.error, "Expense Management is not available for your account.")}
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="expense-manager-page">
      <div className="em-header">
        <div className="d-flex align-items-center gap-4 flex-wrap">
          <h4 className="mb-0">Expenses</h4>
          <Nav pills className="em-tabs">
            {TABS.map((t) => (
              <NavItem key={t.key}>
                <NavLink
                  href="#"
                  active={tab === t.key}
                  onClick={(e) => {
                    e.preventDefault();
                    setTab(t.key);
                  }}
                >
                  {t.label}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        </div>
        <div className="d-flex gap-2">
          {can(meta, PERMISSIONS.CREATE) && (
            <Button color="primary" size="sm" onClick={() => setCreateOpen(true)}>New expense</Button>
          )}
        </div>
      </div>

      <Card className="em-card">
        <CardBody>
          {tab === "dashboard" && <DashboardTab key={refreshKey} meta={meta} />}
          {tab === "expenses" && <ExpensesTab key={refreshKey} meta={meta} onChanged={refreshAll} />}
          {tab === "categories" && <CategoriesTab key={refreshKey} meta={meta} onChanged={refreshAll} />}
          {tab === "recurring" && <RecurringTab key={refreshKey} meta={meta} onChanged={refreshAll} />}
        </CardBody>
      </Card>

      {createOpen && (
        <ExpenseFormModal
          isOpen
          meta={meta}
          toggle={() => setCreateOpen(false)}
          onSaved={() => {
            setCreateOpen(false);
            refreshAll();
          }}
        />
      )}
    </Container>
  );
}
